import type { HitShape } from "./component-registry";
import type { Point } from "./document";
import {
  rectContainsPoint,
  rectsIntersect,
  transformPoint,
  type Matrix2D,
  type Rect,
} from "./geometry";

export type CompiledHitEntry = {
  nodeId: string;
  artboardId: string | null;
  bounds: Rect;
  hitShape: HitShape;
  inverseWorldTransform: Matrix2D;
  locked: boolean;
  selectionRole: "composite" | "slot" | "atomic";
  zIndex: number;
};

export type HitQueryOptions = {
  artboardId?: string;
  includeLocked?: boolean;
};

function polygonContainsPoint(points: readonly Point[], point: Point) {
  let inside = false;
  for (let index = 0, previous = points.length - 1; index < points.length; previous = index++) {
    const currentPoint = points[index]!;
    const previousPoint = points[previous]!;
    const crosses =
      currentPoint.y > point.y !== previousPoint.y > point.y &&
      point.x <
        ((previousPoint.x - currentPoint.x) * (point.y - currentPoint.y)) /
          (previousPoint.y - currentPoint.y) +
          currentPoint.x;
    if (crosses) inside = !inside;
  }
  return inside;
}

function hitShapeContainsPoint(shape: HitShape, point: Point) {
  if (shape.type === "rect") return rectContainsPoint(shape.rect, point);
  if (shape.type === "circle") {
    const deltaX = point.x - shape.centre.x;
    const deltaY = point.y - shape.centre.y;
    return deltaX * deltaX + deltaY * deltaY <= shape.radius * shape.radius;
  }
  return polygonContainsPoint(shape.points, point);
}

function compareHitEntries(first: CompiledHitEntry, second: CompiledHitEntry) {
  return second.zIndex - first.zIndex || first.nodeId.localeCompare(second.nodeId);
}

export class SpatialHitIndex {
  readonly #buckets = new Map<string, CompiledHitEntry[]>();
  readonly #entries: readonly CompiledHitEntry[];
  readonly #cellSize: number;

  constructor(entries: readonly CompiledHitEntry[], cellSize = 25) {
    if (!Number.isFinite(cellSize) || cellSize <= 0) {
      throw new Error("Spatial index cell size must be positive.");
    }
    this.#entries = [...entries];
    this.#cellSize = cellSize;
    for (const entry of entries) {
      for (const key of this.#keysForRect(entry.bounds)) {
        const bucket = this.#buckets.get(key);
        if (bucket) bucket.push(entry);
        else this.#buckets.set(key, [entry]);
      }
    }
  }

  get entries() {
    return this.#entries;
  }

  queryPoint(point: Point, options: HitQueryOptions = {}) {
    const candidates = this.#buckets.get(this.#keyForPoint(point)) ?? [];
    return candidates
      .filter((entry) => this.#matchesOptions(entry, options))
      .filter((entry) => rectContainsPoint(entry.bounds, point))
      .filter((entry) =>
        hitShapeContainsPoint(entry.hitShape, transformPoint(entry.inverseWorldTransform, point)),
      )
      .sort(compareHitEntries);
  }

  queryRect(rect: Rect, options: HitQueryOptions = {}) {
    const candidates = new Map<string, CompiledHitEntry>();
    for (const key of this.#keysForRect(rect)) {
      for (const entry of this.#buckets.get(key) ?? []) candidates.set(entry.nodeId, entry);
    }
    return [...candidates.values()]
      .filter((entry) => this.#matchesOptions(entry, options))
      .filter((entry) => rectsIntersect(entry.bounds, rect))
      .sort(compareHitEntries);
  }

  #matchesOptions(entry: CompiledHitEntry, options: HitQueryOptions) {
    if (!options.includeLocked && entry.locked) return false;
    return options.artboardId === undefined || entry.artboardId === options.artboardId;
  }

  #keyForPoint(point: Point) {
    return `${Math.floor(point.x / this.#cellSize)}:${Math.floor(point.y / this.#cellSize)}`;
  }

  #keysForRect(rect: Rect) {
    const minimumX = Math.floor(rect.x / this.#cellSize);
    const maximumX = Math.floor((rect.x + rect.width) / this.#cellSize);
    const minimumY = Math.floor(rect.y / this.#cellSize);
    const maximumY = Math.floor((rect.y + rect.height) / this.#cellSize);
    const keys: string[] = [];
    for (let x = minimumX; x <= maximumX; x += 1) {
      for (let y = minimumY; y <= maximumY; y += 1) keys.push(`${x}:${y}`);
    }
    return keys;
  }
}
